import { useState, useEffect, useMemo } from 'react';
import { DEFAULT_FORM_DATA } from '../../constants/editQueryModal';
import { normalizeLifecycleValue, filterTestDescription } from '../../utils/editQueryModalUtils';
import { useSnackbar } from '../../components/common/Snackbar';
import {
  hasAssignmentPrivileges,
  isAssignmentActor,
} from '../../utils/assignmentRules';
import {
  MAX_TOTAL_SIZE,
  ALLOWED_FILE_TYPES,
  EXTENSION_MIME_FALLBACK,
  ALLOWED_FILE_TYPES_LABEL,
} from '../../constants/raiseQueryForm';

/**
 * Hook to manage all state for EditQueryModal
 * @param {Object} params - Hook parameters
 * @param {boolean} params.open - Whether modal is open
 * @param {Object} params.query - Query object to edit
 * @returns {Object} State and handlers
 */
export const useEditQueryModalState = ({ open, query }) => {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [focusedFields, setFocusedFields] = useState({});
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [removedAttachments, setRemovedAttachments] = useState([]);
  const [attachmentInputKey, setAttachmentInputKey] = useState(Date.now());
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch (error) {
      // console.error('Failed to parse user from localStorage', error);
      return null;
    }
  });

  // Update current user when modal opens
  useEffect(() => {
    if (!open) return;
    try {
      const storedUser = JSON.parse(localStorage.getItem('user'));
      if (storedUser) {
        setCurrentUser(storedUser);
      }
    } catch (error) {
      // console.error('Failed to parse user from localStorage', error);
    }
  }, [open]);

  // Check if user can manage assignments
  const canManageAssignments = useMemo(() => {
    if (!currentUser || !query) return false;
    if (!hasAssignmentPrivileges(currentUser)) return false;
    const userId = currentUser.id || currentUser._id;
    return isAssignmentActor(userId, query);
  }, [currentUser, query]);

  // Initialize form data when modal opens with query
  useEffect(() => {
    if (open && query) {
      const filteredQueryDesc = filterTestDescription(query.queryDescription || '');
      
      setFormData({
        queryDescription: filteredQueryDesc,
        visualDescription: query.visualDescription || '',
        priority: query.priority || 'medium',
        category: query.category || '',
        statusLabel: normalizeLifecycleValue(query.statusLabel || query.status || 'raised'),
        statusResponse: query.statusResponse || '',
        assigneeId: query.assignee?._id || '',
      });
      setExistingAttachments(Array.isArray(query.attachments) ? query.attachments : []);
      setNewFiles([]);
      setRemovedAttachments([]);
      setErrors({});
      setFocusedFields({});
    }
  }, [open, query]);

  const isValidFileType = (file) => {
    if (ALLOWED_FILE_TYPES.includes(file.type)) return true;

    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    return ext in EXTENSION_MIME_FALLBACK;
  };

  const validateFiles = (files) => {
    const validFiles = [];
    const errorsFound = [];
    const currentTotalSize = newFiles.reduce((sum, file) => sum + file.size, 0);

    files.forEach((file) => {
      if (!isValidFileType(file)) {
        errorsFound.push(`${file.name}: Only ${ALLOWED_FILE_TYPES_LABEL} files are allowed`);
        return;
      }

      const incomingSize = validFiles.reduce((sum, item) => sum + item.size, 0) + file.size;
      if (currentTotalSize + incomingSize > MAX_TOTAL_SIZE) {
        errorsFound.push(`Total file size exceeds ${MAX_TOTAL_SIZE / (1024 * 1024)}MB limit (all files combined)`);
        return;
      }

      validFiles.push(file);
    });

    return { validFiles, errorsFound };
  };

  const handleNewFilesChange = (files) => {
    const selectedFiles = Array.from(files || []);
    if (selectedFiles.length === 0) return;

    const { validFiles, errorsFound } = validateFiles(selectedFiles);

    if (errorsFound.length > 0) {
      showSnackbar(errorsFound.join('. '), 'error', { duration: 5000 });
    }

    if (validFiles.length > 0) {
      setNewFiles((prev) => [...prev, ...validFiles]);
      setAttachmentInputKey(Date.now());
    }
  };

  const handleRemoveNewFile = (index) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingAttachment = (attachment) => {
    const attachmentId = attachment?._id ? attachment._id.toString() : '';
    const attachmentPath = attachment?.filePath || attachment?.fileUrl;

    setExistingAttachments((prev) =>
      prev.filter((item) => {
        const itemId = item?._id ? item._id.toString() : '';
        if (attachmentId && itemId) {
          return itemId !== attachmentId;
        }
        return (item?.filePath || item?.fileUrl) !== attachmentPath;
      })
    );

    setRemovedAttachments((prev) => {
      const alreadyAdded = prev.some((item) => {
        const existingId = item?._id ? item._id.toString() : '';
        if (attachmentId && existingId) {
          return existingId === attachmentId;
        }
        return (item?.filePath || item?.fileUrl) === attachmentPath;
      });

      if (alreadyAdded) return prev;

      return [
        ...prev,
        {
          _id: attachmentId,
          filePath: attachment?.filePath,
          fileUrl: attachment?.fileUrl,
        },
      ];
    });
  };

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
    if (errors[field]) setErrors({ ...errors, [field]: '' });
  };

  const handlePriorityChange = (event, newValue) => {
    const value = newValue ? newValue.value : '';
    setFormData({ ...formData, priority: value });
    if (errors.priority) setErrors({ ...errors, priority: '' });
  };

  const handleAssigneeChange = (event, newValue) => {
    const value = newValue ? newValue._id : '';
    setFormData({ ...formData, assigneeId: value });
    if (errors.assigneeId) setErrors({ ...errors, assigneeId: '' });
  };

  const handleFocus = (field) => () => {
    setFocusedFields({ ...focusedFields, [field]: true });
  };

  const handleBlur = (field) => () => {
    setFocusedFields({ ...focusedFields, [field]: false });
  };

  return {
    loading,
    setLoading,
    formData,
    setFormData,
    errors,
    setErrors,
    focusedFields,
    existingAttachments,
    newFiles,
    removedAttachments,
    attachmentInputKey,
    currentUser,
    canManageAssignments,
    handleChange,
    handlePriorityChange,
    handleAssigneeChange,
    handleFocus,
    handleBlur,
    handleNewFilesChange,
    handleRemoveNewFile,
    handleRemoveExistingAttachment,
  };
};
